import React, { useEffect, useState, useMemo } from 'react';
import { useFacilitiesPage } from '../services/api/domains/facilities/hooks';
import useAuth from 'auth/auth';
import {
  Product,
  UploadDifferences,
  ProductChangeRecord,
} from 'services/api/domains/products/types';
import { useManufacturersPage } from '../services/api/domains/manufacturers/hooks';
import {
  useProductsPage,
  useUploadProducts,
  useCommitProductChanges,
} from '../services/api/domains/products/hooks';
import { ApiStateBase } from 'services/api/types';
import ProductModal from 'components/modals/ProductModal';
import { ModalMode } from 'components/modals/hooks/useEditableModal';
import { useGradesPage } from 'services/api/domains/grades/hooks';
import { useTherapeuticAreasPage } from 'services/api/domains/therapeutic-areas/hooks';
import UploadProductsModal from 'components/modals/UploadProductsModal';
import LgmDataGrid from '../components/LgmDataGrid';
import TableSearch from '../components/TableSearch';
import {
  SearchMode,
  AdvancedSearchQuery,
  createEmptyAdvancedQuery,
} from '../components/TableSearch/types';
import { productsFieldConfig } from '../config/searchConfigs';
import {
  GridColDef,
  GridPaginationModel,
  GridSortModel,
  GridFilterModel,
  GridFilterInputValueProps,
} from '@mui/x-data-grid';
import {
  gridFilterToAdvancedQuery,
  mergeAdvancedQueries,
} from '../utils/dataGridHelpers';
import ExpandableCell, {
  ExpandableListCell,
} from '../components/ExpandableCell';
import { TextField } from '@mui/material';

const PAGE_SIZE = 50;

interface ProductsReviewProps {
  deleteAllStatus: ApiStateBase<string>;
}

// Type for normalized change items
interface NormalizedChange {
  field: string;
  oldValue: string | undefined;
  newValue: string | undefined;
}

const ProductsReview: React.FC<ProductsReviewProps> = ({ deleteAllStatus }) => {
  const { isAdmin, isEditor, isViewer } = useAuth();

  const highLimitParam = useMemo(() => ({ limit: 1000 }), []);

  // Fetch ALL manufacturers without pagination for the modal options
  const manufacturersManager = useManufacturersPage(highLimitParam);
  const { data: facilitiesRaw } = useFacilitiesPage(highLimitParam);
  const { data: gradesRaw } = useGradesPage();
  const { data: therapeuticAreasRaw } = useTherapeuticAreasPage();

  const facilities = Array.isArray(facilitiesRaw) ? facilitiesRaw : [];
  const grades = Array.isArray(gradesRaw) ? gradesRaw : [];
  const therapeuticAreas = Array.isArray(therapeuticAreasRaw)
    ? therapeuticAreasRaw
    : [];

  // Search state
  const [searchMode, setSearchMode] = useState<SearchMode>('basic');
  const [basicSearchTerm, setBasicSearchTerm] = useState('');
  const [advancedSearchQuery, setAdvancedSearchQuery] =
    useState<AdvancedSearchQuery>(createEmptyAdvancedQuery());

  // DataGrid state
  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [gridSortModel, setGridSortModel] = useState<GridSortModel>([]);
  const [gridPaginationModel, setGridPaginationModel] =
    useState<GridPaginationModel>({
      page: 0,
      pageSize: PAGE_SIZE,
    });

  const productsParams = useMemo(() => {
    let finalAdvancedQuery = createEmptyAdvancedQuery();

    // Start with TableSearch query (basic or advanced)
    if (searchMode === 'advanced') {
      finalAdvancedQuery = advancedSearchQuery;
    }

    if (gridFilterModel.items.length > 0) {
      const gridQuery = gridFilterToAdvancedQuery(
        gridFilterModel,
        productsFieldConfig
      );

      if (gridQuery.filterGroups.length > 0) {
        finalAdvancedQuery = mergeAdvancedQueries(
          finalAdvancedQuery,
          gridQuery
        );
      }
    }

    // Check if there are actual filter rules, not just empty groups
    const hasActualFilters = finalAdvancedQuery.filterGroups.some(
      group => group.rules && group.rules.length > 0
    );

    const params = {
      search:
        searchMode === 'basic'
          ? basicSearchTerm.trim() || undefined
          : undefined,
      advancedSearch: hasActualFilters ? finalAdvancedQuery : undefined,
      sortBy:
        gridSortModel.length > 0
          ? (gridSortModel[0].field as keyof Product)
          : undefined,
      sortDirection:
        gridSortModel.length > 0
          ? (gridSortModel[0].sort?.toUpperCase() as 'ASC' | 'DESC')
          : undefined,
      limit: gridPaginationModel.pageSize,
      offset: gridPaginationModel.page * gridPaginationModel.pageSize,
    };

    return params;
  }, [
    searchMode,
    basicSearchTerm,
    advancedSearchQuery,
    gridFilterModel,
    gridSortModel,
    gridPaginationModel,
  ]);

  const {
    data: products,
    total,
    refetch: refetchProducts,
    isLoading: productsLoading,
    isError: productsError,
    error: productsErrorMessage,
    delete: deleteProduct,
    update: updateProduct,
  } = useProductsPage(productsParams);

  // Preserve total during loading to prevent DataGrid from resetting pagination
  // When loading starts, total becomes 0, causing DataGrid to think page is invalid
  // Use state instead of ref so updates trigger re-renders
  const [stableTotal, setStableTotal] = useState(total || 0);
  useEffect(() => {
    if (total && total > 0) {
      setStableTotal(total);
    }
  }, [total, stableTotal]);

  const uploadProductsMutation = useUploadProducts();
  const commitChangesMutation = useCommitProductChanges();

  const [openProductModal, setOpenProductModal] = useState<ModalMode | null>(
    null
  );
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const [openMfgPick, setOpenMfgPick] = useState(false);
  const [chosenManufacturer, setChosenManufacturer] = useState<number | null>(
    null
  );

  const [diffData, setDiffData] = useState<UploadDifferences | null>(null);
  const [openDiffModal, setOpenDiffModal] = useState(false);

  // Custom filter operators that work with our backend
  const stringFilterOperators = useMemo(
    () => [
      {
        label: 'contains',
        value: 'contains',
        getApplyFilterFn: () => null,
        InputComponent: (props: GridFilterInputValueProps) => (
          <TextField
            {...props}
            variant="standard"
            placeholder="Filter value"
            onChange={e =>
              props.applyValue({ ...props.item, value: e.target.value })
            }
          />
        ),
      },
      {
        label: 'equals',
        value: 'equals',
        getApplyFilterFn: () => null,
        InputComponent: (props: GridFilterInputValueProps) => (
          <TextField
            {...props}
            variant="standard"
            placeholder="Filter value"
            onChange={e =>
              props.applyValue({ ...props.item, value: e.target.value })
            }
          />
        ),
      },
      {
        label: 'not equals',
        value: 'notEquals',
        getApplyFilterFn: () => null,
        InputComponent: (props: GridFilterInputValueProps) => (
          <TextField
            {...props}
            variant="standard"
            placeholder="Filter value"
            onChange={e =>
              props.applyValue({ ...props.item, value: e.target.value })
            }
          />
        ),
      },
    ],
    []
  );

  // DataGrid columns - use MUI DataGrid format
  const productsDataGridColumns = useMemo<GridColDef<Product>[]>(
    () => [
      {
        field: 'product_name',
        headerName: 'Product Name',
        width: 250,
        flex: 1,
        sortable: true,
        filterable: true,
        filterOperators: stringFilterOperators,
        renderCell: params => {
          const name =
            params.row.preferred_product_name || params.row.product_name;
          return (
            <ExpandableCell content={name} isHtml={true} maxLength={100} />
          );
        },
      },
      {
        field: 'grades',
        headerName: 'Product Type',
        width: 150,
        sortable: true,
        filterable: false,
        renderCell: params => {
          const gradeNames = params.row.grades?.map(g => g.grade_name) || [];
          return <ExpandableListCell items={gradeNames} maxItems={2} />;
        },
      },
      {
        field: 'therapeutic_areas',
        headerName: 'Therapeutic Area',
        width: 200,
        sortable: true,
        filterable: false,
        renderCell: params => {
          const taNames =
            params.row.therapeutic_areas?.map(ta => ta.therapeutic_area_name) ||
            [];
          return <ExpandableListCell items={taNames} maxItems={2} />;
        },
      },
      {
        field: 'cas_number',
        headerName: 'CAS#',
        width: 150,
        sortable: true,
        filterable: true,
        filterOperators: stringFilterOperators,
      },
      {
        field: 'manufacturers',
        headerName: 'Manufacturers',
        width: 300,
        flex: 1,
        sortable: true,
        filterable: false,
        renderCell: params => {
          const mfgStrings =
            params.row.manufacturers?.map(
              m => `${m.manufacturer_name} | ${m.manufacturing_status || 'N/A'}`
            ) || [];
          return <ExpandableListCell items={mfgStrings} maxItems={2} />;
        },
      },
      {
        field: 'note',
        headerName: 'Note',
        width: 250,
        sortable: true,
        filterable: true,
        filterOperators: stringFilterOperators,
        renderCell: params => (
          <ExpandableCell
            content={params.row.note || ''}
            isHtml={false}
            maxLength={100}
          />
        ),
      },
    ],
    [stringFilterOperators]
  );

  // Reset DataGrid page when search changes
  // Only reset when there's ACTUAL search content
  useEffect(() => {
    const hasBasicSearch =
      searchMode === 'basic' && basicSearchTerm.trim().length > 0;
    const hasAdvancedFilters = advancedSearchQuery.filterGroups.some(
      group => group.rules && group.rules.length > 0
    );
    const hasAdvancedSearch = searchMode === 'advanced' && hasAdvancedFilters;

    if (hasBasicSearch || hasAdvancedSearch) {
      setGridPaginationModel(prev => ({ ...prev, page: 0 }));
    }
  }, [basicSearchTerm, searchMode, advancedSearchQuery]);

  useEffect(() => {
    if (deleteAllStatus.status === 'success') {
      refetchProducts();
    }
  }, [deleteAllStatus.status, refetchProducts]);

  const handleConfirmManufacturer = async (
    chosenManufacturer: number | null,
    uploadFile: File | null
  ) => {
    if (!uploadFile || !chosenManufacturer)
      return alert('❌ Select manufacturer and file.');
    try {
      setChosenManufacturer(chosenManufacturer);
      const result = await uploadProductsMutation.mutate({
        file: uploadFile,
        manufacturer_ids: [chosenManufacturer],
        facility_ids: [],
      });
      if (result.hasDifferences && result.differences) {
        setDiffData(result.differences);
        setOpenDiffModal(true);
      } else {
        alert('✅ Upload complete.');
        setOpenMfgPick(false);
        refetchProducts();
      }
    } catch (error) {
      console.error('❌ Upload failed:', error);
      alert('Upload failed. Please try again.');
    }
  };

  const handleCommitChanges = async () => {
    if (!chosenManufacturer || !diffData) return;
    try {
      await commitChangesMutation.mutate({
        manufacturer_ids: [chosenManufacturer],
        facility_ids: [],
        differences: diffData,
      });
      alert('✅ Changes committed.');
      setOpenDiffModal(false);
      setDiffData(null);
      setOpenMfgPick(false);
      refetchProducts();
    } catch (error) {
      console.error('❌ Commit failed:', error);
      alert('Commit failed. Please try again.');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete product?')) return;
    await deleteProduct.mutate(id);
    refetchProducts();
  };

  const handleSaveEdit = async (editedProduct: Product) => {
    if (!editedProduct || !editedProduct.product_id) return;
    try {
      await updateProduct.mutate({
        ...editedProduct,
        manufacturers: Array.isArray(editedProduct.manufacturers)
          ? editedProduct.manufacturers.map(m => ({
              manufacturer_id: m.manufacturer_id,
              manufacturing_status: m.manufacturing_status || null,
            }))
          : [],
        facility_ids: Array.isArray(editedProduct.facilities)
          ? editedProduct.facilities.map(f => f.facility_id)
          : [],
        grade_ids: Array.isArray(editedProduct.grades)
          ? editedProduct.grades.map(g => g.grade_id)
          : [],
        therapeutic_area_ids: Array.isArray(editedProduct.therapeutic_areas)
          ? editedProduct.therapeutic_areas.map(ta => ta.therapeutic_area_id)
          : [],
      });
      setOpenProductModal(null);
      setSelectedProduct(null);
      refetchProducts();
    } catch (error) {
      console.error('❌ Update failed:', error);
      alert('❌ Failed to update product. Please try again.');
    }
  };

  // normalize Changed Products
  const normalizeChangedProducts = (
    items: ProductChangeRecord[]
  ): NormalizedChange[] =>
    (items || []).flatMap((c: ProductChangeRecord) => {
      const before = c.before;
      const after = c.after;
      const diffs: NormalizedChange[] = [];

      if (before.product_name !== after['PRODUCT NAME'])
        diffs.push({
          field: 'Product Name',
          oldValue: before.product_name,
          newValue: after['PRODUCT NAME'],
        });
      if (before.cas_number !== after['CAS NUMBER'])
        diffs.push({
          field: 'CAS #',
          oldValue: before.cas_number,
          newValue: after['CAS NUMBER'],
        });
      // TODO: Update diff utility to handle grade and therapeutic area names
      // Note: grade_name and therapeutic_area_name are not available in ExistingProductRecord
      // The API returns grade_id and therapeutic_area_id instead
      // if (after['GRADE'])
      //   diffs.push({
      //     field: 'Grade',
      //     oldValue: 'Previous Grade ID: ' + before.grade_id,
      //     newValue: after['GRADE'],
      //   });
      // if (after['THERAPEUTIC AREA'])
      //   diffs.push({
      //     field: 'Therapeutic Area',
      //     oldValue: 'Previous TA ID: ' + before.therapeutic_area_id,
      //     newValue: after['THERAPEUTIC AREA'],
      //   });
      if (before.note !== after['NOTE'])
        diffs.push({
          field: 'Note',
          oldValue: before.note,
          newValue: after['NOTE'],
        });
      return diffs;
    });

  const changedItems = normalizeChangedProducts(diffData?.changed || []);

  return (
    <div style={styles.pageContainerStyle}>
      <div style={styles.searchBarContainerStyle}>
        <div style={styles.searchUploadContainerStyle}>
          <TableSearch
            mode={searchMode}
            onModeChange={setSearchMode}
            basicSearchTerm={basicSearchTerm}
            onBasicSearchChange={term => {
              setBasicSearchTerm(term);
            }}
            onBasicSearchClear={() => {
              setBasicSearchTerm('');
            }}
            basicPlaceholder="Search products..."
            advancedFieldConfigs={productsFieldConfig}
            advancedQuery={advancedSearchQuery}
            onAdvancedSearch={query => {
              setAdvancedSearchQuery(query);
            }}
            onAdvancedClear={() => {
              setAdvancedSearchQuery(createEmptyAdvancedQuery());
            }}
          />
          {(isAdmin || isEditor) && (
            <button
              onClick={() => setOpenMfgPick(true)}
              style={styles.uploadButtonStyle}>
              Upload Product Sheet
            </button>
          )}
        </div>
      </div>

      <div style={styles.tableContainerStyle}>
        <LgmDataGrid<Product>
          data={products}
          columns={productsDataGridColumns}
          loading={productsLoading}
          rowCount={stableTotal}
          paginationModel={gridPaginationModel}
          onPaginationModelChange={setGridPaginationModel}
          sortModel={gridSortModel}
          onSortModelChange={model => {
            // MUI DataGrid cycles: none -> ASC -> DESC -> none
            // We want to cycle: ASC -> DESC -> ASC (no unsorted state)
            if (model.length === 0 && gridSortModel.length > 0) {
              // User clicked to unsort, flip to opposite direction instead
              const currentSort = gridSortModel[0];
              const newDirection = currentSort.sort === 'asc' ? 'desc' : 'asc';
              setGridSortModel([
                { field: currentSort.field, sort: newDirection },
              ]);
            } else {
              setGridSortModel(model);
            }
          }}
          filterModel={gridFilterModel}
          onFilterModelChange={setGridFilterModel}
          getRowId={row => row.product_id}
          onInfo={(p: Product) => {
            setSelectedProduct(p);
            setOpenProductModal('VIEW');
          }}
          onEdit={
            !isViewer
              ? (p: Product) => {
                  setSelectedProduct(p);
                  setOpenProductModal('EDIT');
                }
              : undefined
          }
          onDelete={
            !isViewer
              ? (p: Product) => {
                  handleDelete(p.product_id);
                }
              : undefined
          }
        />

        {/* Upload Modal */}
        {openMfgPick && (
          <UploadProductsModal
            isLoading={uploadProductsMutation.status === 'loading'}
            confirmManufacturer={handleConfirmManufacturer}
            closeModal={() => setOpenMfgPick(false)}
          />
        )}

        {/* Diff Modal */}
        {openDiffModal && diffData && (
          <div style={styles.modalOverlayStyle}>
            <div style={styles.diffModalContentStyle}>
              <h2 style={styles.diffModalTitleStyle}>Differences Found</h2>

              {/* Summary */}
              <div style={styles.diffSummaryStyle}>
                <p>
                  <strong>New Products:</strong> {diffData.new?.length || 0}
                </p>
                <p>
                  <strong>Changed Products:</strong>{' '}
                  {diffData.changed?.length || 0}
                </p>
                <p>
                  <strong>Deleted Products:</strong>{' '}
                  {diffData.deleted?.length || 0}
                </p>
              </div>

              {/* New Products */}
              {diffData.new && diffData.new.length > 0 && (
                <div style={styles.diffSectionStyle}>
                  <h3 style={styles.newProductsHeaderStyle}>
                    New Products ({diffData.new.length})
                  </h3>
                  <table style={styles.diffTableStyle}>
                    <thead style={styles.newProductsTheadStyle}>
                      <tr>
                        <th style={styles.diffTableHeaderStyle}>
                          Product Name
                        </th>
                        <th style={styles.diffTableHeaderStyle}>CAS Number</th>
                        <th style={styles.diffTableHeaderStyle}>
                          Product Type
                        </th>
                        <th style={styles.diffTableHeaderStyle}>
                          Therapeutic Area
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {diffData.new.map((item, i) => (
                        <tr
                          key={
                            item['PRODUCT NAME'] +
                            '-' +
                            (item['CAS NUMBER'] || '') +
                            '-' +
                            i
                          }>
                          <td style={styles.diffTableCellStyle}>
                            {item['PRODUCT NAME']}
                          </td>
                          <td style={styles.diffTableCellStyle}>
                            {item['CAS NUMBER'] || ''}
                          </td>
                          <td style={styles.diffTableCellStyle}>
                            {item['GRADE'] || ''}
                          </td>
                          <td style={styles.diffTableCellStyle}>
                            {item['THERAPEUTIC AREA'] || ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Changed Products */}
              {changedItems.length > 0 && (
                <div style={styles.diffSectionStyle}>
                  <h3 style={styles.changedProductsHeaderStyle}>
                    Changed Products ({changedItems.length} changes)
                  </h3>
                  <table style={styles.diffTableStyle}>
                    <thead style={styles.changedProductsTheadStyle}>
                      <tr>
                        <th style={styles.diffTableHeaderStyle}>Field</th>
                        <th style={styles.diffTableHeaderStyle}>Old</th>
                        <th style={styles.diffTableHeaderStyle}>New</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changedItems.map((c, i) => (
                        <tr
                          key={
                            c.field +
                            '-' +
                            (c.oldValue ?? '') +
                            '-' +
                            (c.newValue ?? '') +
                            '-' +
                            i
                          }>
                          <td style={styles.diffTableCellStyle}>{c.field}</td>
                          <td style={styles.diffTableCellOldStyle}>
                            {c.oldValue}
                          </td>
                          <td style={styles.diffTableCellNewStyle}>
                            {c.newValue}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Deleted Products */}
              {diffData.deleted && diffData.deleted.length > 0 && (
                <div style={styles.diffSectionStyle}>
                  <h3 style={styles.deletedProductsHeaderStyle}>
                    Deleted Products ({diffData.deleted.length})
                  </h3>
                  <table style={styles.diffTableStyle}>
                    <thead style={styles.deletedProductsTheadStyle}>
                      <tr>
                        <th style={styles.diffTableHeaderStyle}>
                          Product Name
                        </th>
                        <th style={styles.diffTableHeaderStyle}>CAS Number</th>
                        <th style={styles.diffTableHeaderStyle}>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diffData.deleted.map((item, i) => (
                        <tr
                          key={
                            item.product_name +
                            '-' +
                            (item.cas_number || '') +
                            '-' +
                            i
                          }>
                          <td style={styles.diffTableCellStyle}>
                            {item.product_name}
                          </td>
                          <td style={styles.diffTableCellStyle}>
                            {item.cas_number || ''}
                          </td>
                          <td style={styles.diffTableCellStyle}>
                            {item.note || ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={styles.diffModalActionsStyle}>
                <button
                  onClick={() => setOpenDiffModal(false)}
                  style={styles.diffCancelButtonStyle}>
                  Cancel
                </button>
                <button
                  onClick={handleCommitChanges}
                  style={styles.diffCommitButtonStyle}
                  disabled={commitChangesMutation.status === 'loading'}>
                  {commitChangesMutation.status === 'loading'
                    ? 'Applying…'
                    : 'Apply'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {/* {productsLoading && (
          <div style={styles.loadingContainerStyle}>
            <span style={styles.loadingTextStyle}>Loading products...</span>
          </div>
        )} */}

        {/* Error State */}
        {productsError && (
          <div style={styles.errorContainerStyle}>
            Error loading products: {productsErrorMessage?.message}
          </div>
        )}

        {/* All your existing Upload, Diff, Display, and Edit Modals remain below, unchanged */}

        {/* Display Modal */}
        {openProductModal !== null && selectedProduct && (
          <ProductModal
            product={selectedProduct}
            initialMode={openProductModal}
            save={handleSaveEdit}
            close={() => setOpenProductModal(null)}
            allManufacturers={manufacturersManager.data}
            allFacilities={facilities || []}
            allGrades={grades || []}
            allTherapeuticAreas={therapeuticAreas || []}
          />
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  titleStyle: {
    fontSize: '1.25rem', // text-xl
    fontWeight: 'bold',
  } as React.CSSProperties,
  searchUploadContainerStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  uploadButtonStyle: {
    padding: '8px 16px', // px-4 py-2
    borderRadius: '4px', // rounded
    backgroundColor: '#2563eb', // bg-blue-600
    color: '#ffffff', // text-white
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.8125rem',
  },
  modalOverlayStyle: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // bg-black bg-opacity-50
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
  },
  modalTitleStyle: {
    fontSize: '18px', // text-lg
    fontWeight: 'bold', // font-bold
    marginBottom: '16px', // mb-4
  },
  manufacturerListStyle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px', // space-y-2
    maxHeight: '240px', // max-h-60
    overflowY: 'auto', // overflow-y-auto
  },
  manufacturerButtonStyle: {
    display: 'block',
    width: '100%',
    textAlign: 'left' as const,
    padding: '8px 12px', // px-3 py-2
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px', // rounded
    cursor: 'pointer',
  },
  modalButtonContainerStyle: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px', // space-x-2
    marginTop: '16px', // mt-4
  },
  cancelButtonStyle: {
    padding: '8px 16px', // px-4 py-2
    backgroundColor: '#d1d5db', // bg-gray-300
    color: '#374151', // text-gray-700
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  diffModalContentStyle: {
    backgroundColor: '#ffffff', // bg-white
    padding: '24px', // p-6
    borderRadius: '8px', // rounded-lg
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', // shadow-lg
    width: '900px', // w-[900px]
    maxHeight: '80vh', // max-h-[80vh]
    overflowY: 'auto', // overflow-y-auto
  },
  diffModalHeaderStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px', // mb-4
  },
  diffModalTitleStyle: {
    fontSize: '20px', // text-xl
    fontWeight: 'bold', // font-bold
  },
  closeButtonStyle: {
    color: '#6b7280', // text-gray-500
    fontSize: '20px', // text-xl
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
  buttonEnabled: {
    color: '#d97706', // text-yellow-600
    cursor: 'pointer',
  },
  buttonDisabled: {
    color: '#9ca3af', // text-gray-400
    cursor: 'not-allowed',
  },
  deleteButtonEnabled: {
    color: '#dc2626', // text-red-600
    cursor: 'pointer',
  },
  diffSummaryStyle: {
    marginBottom: '16px', // mb-4
    padding: '12px', // p-3
    backgroundColor: '#f3f4f6', // bg-gray-100
    borderRadius: '4px', // rounded
  },
  diffSectionStyle: {
    marginBottom: '16px', // mb-4
  },
  newProductsHeaderStyle: {
    fontWeight: '600', // font-semibold
    color: '#059669', // text-green-600
    marginBottom: '8px', // mb-2
  },
  diffTableStyle: {
    width: '100%',
    border: '1px solid #d1d5db',
    fontSize: '14px', // text-sm
    marginBottom: '16px', // mb-4
  },
  newProductsTheadStyle: {
    backgroundColor: '#d1fae5', // bg-green-100
  },
  diffTableHeaderStyle: {
    border: '1px solid #d1d5db',
    padding: '8px', // p-2
    textAlign: 'left' as const,
  },
  diffTableCellStyle: {
    border: '1px solid #d1d5db',
    padding: '8px', // p-2
  },
  changedProductsHeaderStyle: {
    fontWeight: '600', // font-semibold
    color: '#d97706', // text-yellow-600
    marginBottom: '8px', // mb-2
  },
  changedProductsTheadStyle: {
    backgroundColor: '#fef3c7', // bg-yellow-100
  },
  diffTableCellOldStyle: {
    border: '1px solid #d1d5db',
    padding: '8px', // p-2
    color: '#dc2626', // text-red-600
  },
  diffTableCellNewStyle: {
    border: '1px solid #d1d5db',
    padding: '8px', // p-2
    color: '#059669', // text-green-600
  },
  deletedProductsHeaderStyle: {
    fontWeight: '600', // font-semibold
    color: '#dc2626', // text-red-600
    marginBottom: '8px', // mb-2
  },
  deletedProductsTheadStyle: {
    backgroundColor: '#fee2e2', // bg-red-100
  },
  diffModalActionsStyle: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px', // space-x-2
  },
  diffCancelButtonStyle: {
    backgroundColor: '#9ca3af', // bg-gray-400
    color: '#ffffff', // text-white
    padding: '8px 16px', // px-4 py-2
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  diffCommitButtonStyle: {
    backgroundColor: '#059669', // bg-green-600
    color: '#ffffff', // text-white
    padding: '8px 16px', // px-4 py-2
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  loadingContainerStyle: {
    textAlign: 'center',
    padding: '16px 0', // py-4
  },
  loadingTextStyle: {
    color: '#4b5563', // text-gray-600
  },
  errorContainerStyle: {
    backgroundColor: '#fecaca', // bg-red-100
    border: '1px solid #f87171', // border-red-400
    color: '#b91c1c', // text-red-700
    padding: '12px 16px', // px-4 py-3
    borderRadius: '4px', // rounded
    marginBottom: '16px', // mb-4
  },
  mainTableStyle: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    border: '1px solid #d1d5db', // border-gray-300
  },
  mainTableHeaderStyle: {
    backgroundColor: '#f3f4f6', // bg-gray-100
  },
  sortableHeaderStyle: {
    border: '1px solid #d1d5db',
    padding: '8px', // p-2
    textAlign: 'left' as const,
    cursor: 'pointer',
  },
  mainTableNonSortableHeaderStyle: {
    border: '1px solid #d1d5db',
    padding: '8px', // p-2
    textAlign: 'left' as const,
  },
  mainTableCellStyle: {
    border: '1px solid #d1d5db',
    padding: '8px', // p-2
  },
  actionButtonsContainerStyle: {
    display: 'flex',
    gap: '8px', // space-x-2
  },
  infoButtonStyle: {
    color: '#2563eb', // text-blue-600
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
  },
  paginationContainerStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px', // mt-4
  },
  paginationButtonStyle: {
    padding: '4px 12px', // px-3 py-1
    backgroundColor: '#e5e7eb', // bg-gray-200
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  displayModalContentStyle: {
    backgroundColor: '#ffffff',
    padding: '24px', // p-6
    borderRadius: '8px', // rounded-lg
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // shadow-lg
    width: '384px', // w-96
  },
  displayModalCloseButtonStyle: {
    padding: '8px 16px', // px-4 py-2
    backgroundColor: '#9ca3af', // bg-gray-400
    color: '#ffffff', // text-white
    borderRadius: '4px', // rounded
    marginTop: '16px', // mt-4
    border: 'none',
    cursor: 'pointer',
  },
  editModalContentStyle: {
    backgroundColor: '#ffffff',
    padding: '24px', // p-6
    borderRadius: '8px', // rounded-lg
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // shadow-lg
    width: '384px', // w-96
  },
  editModalInputStyle: {
    border: '1px solid #d1d5db',
    padding: '8px', // p-2
    marginBottom: '8px', // mb-2
    width: '100%',
  },
  editModalTextareaStyle: {
    border: '1px solid #d1d5db',
    padding: '8px', // p-2
    marginBottom: '8px', // mb-2
    width: '100%',
    resize: 'vertical' as const,
  },
  editModalActionsStyle: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px', // space-x-2
  },
  editModalCancelButtonStyle: {
    padding: '8px 16px', // px-4 py-2
    backgroundColor: '#9ca3af', // bg-gray-400
    color: '#ffffff', // text-white
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  editModalSaveButtonStyle: {
    padding: '8px 16px', // px-4 py-2
    backgroundColor: '#059669', // bg-green-600
    color: '#ffffff', // text-white
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  uploadContentStyle: {
    marginBottom: '16px',
  },
  uploadInfoStyle: {
    marginBottom: '12px',
    fontWeight: '600',
    color: '#374151',
  },
  fileInputStyle: {
    marginBottom: '12px',
    padding: '8px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    width: '100%',
  },
  uploadFileInfoStyle: {
    fontSize: '14px',
    color: '#6b7280',
    fontStyle: 'italic',
  },
  confirmButtonStyle: {
    padding: '8px 16px', // px-4 py-2
    backgroundColor: '#059669', // bg-green-600
    color: '#ffffff', // text-white
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  confirmButtonDisabledStyle: {
    padding: '8px 16px', // px-4 py-2
    backgroundColor: '#d1d5db', // disabled gray
    color: '#9ca3af', // disabled text
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'not-allowed',
  },
  pageContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  } as React.CSSProperties,
  searchBarContainerStyle: {
    flexShrink: 0,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #d1d5db',
    paddingBottom: '8px',
  } as React.CSSProperties,
  tableContainerStyle: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  } as React.CSSProperties,
};

export default ProductsReview;
